import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useWatch } from "antd/es/form/Form";
import { track } from "@amplitude/analytics-browser";
import ResponsiveSheet from "src/components/ResponsiveSheet/ResponsiveSheet";
import { useCheckoutMutation } from "src/store/apis/pointsExchange";
import { formulateOrderRequest, getPointsAfterConversion } from "../../utils";
import { ExchangeForm, ExchangeModalContainerProps, ExchangeResultStep, ExchangeTriggerButton } from "../ExchangeModalForm/ExchangeModalForm";

const EXCHANGE_MODAL_WIDTH = 520;

const ExchangeModal: FC<ExchangeModalContainerProps> = ({ destination, exchangeInfo, termsAndConditions }) => {
  const { t } = useTranslation();
  const [request, { isLoading, error, data, reset }] = useCheckoutMutation();
  const formInstance = useFormInstance();
  const points = useWatch("points", formInstance);
  const requiredFieldInputs = useWatch("RequiredFields", formInstance);
  const hasResult = !!error || !!data;
  const { sentValue: pointsAfter } = getPointsAfterConversion(points, exchangeInfo.conversionRate, exchangeInfo.fees);

  const handleSubmit = () => {
    const orderRequest = formulateOrderRequest({
      pointsBefore: points,
      program: destination?.value ?? "",
      exchangeInfo,
      requiredFieldsInputs: requiredFieldInputs,
    });
    track("Checkout Submitted", { ...orderRequest, destination });
    request(orderRequest);
  };

  const handleOpen = (open: () => void) => {
    formInstance
      .validateFields()
      .then(() => {
        track("Checkout Initiated", { destination, exchangeInfo, points });
        reset();
        open();
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!error && !data) return;
    track("Checkout Result Viewed", { success: !error, error, data, destination });
  }, [error, data]);

  return (
    <ResponsiveSheet
      showTitle={!hasResult}
      label={
        <div className="flex flex-col">
          <span className="font-semibold">{t("PointsExchange.exchange.pointsExchangeTitle")}</span>
          <span className="text-font-subtle text-sm font-normal">{t("PointsExchange.exchange.reviewExchange")}</span>
        </div>
      }
      modalProps={{ width: EXCHANGE_MODAL_WIDTH, afterClose: reset }}
      drawerProps={{ afterOpenChange: (isOpen) => !isOpen && reset() }}
      renderTrigger={({ open }) => <ExchangeTriggerButton onOpen={() => handleOpen(open)} />}
      renderSheet={({ close }) =>
        hasResult ? (
          <ExchangeResultStep error={error} data={data} termsAndConditions={termsAndConditions} onBack={reset} onClose={close} />
        ) : (
          <ExchangeForm
            destination={destination}
            points={points}
            pointsAfter={pointsAfter}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            onCancel={close}
          />
        )
      }
    />
  );
};

export default ExchangeModal;
